import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { step2Schema, type Step2Data } from '@/lib/validations';

const organizationTypes = [
  'Federal Government Agency',
  'State Government Agency',
  'Local Government / Municipality',
  'Small Business (< 50 employees)',
  'Mid-Size Business (50-500 employees)',
  'Enterprise (500+ employees)',
  'Agency / Marketing Partner',
  'Nonprofit Organization',
  'Other',
];

const roleOptions = [
  'Marketing / Communications Lead',
  'Executive / Director',
  'Program Manager',
  'Procurement / Contracting',
  'Business Owner / Founder',
  'Agency Account Manager',
  'Other',
  'Will discuss',
];

interface Step2OrganizationProps {
  initialData?: Partial<Step2Data>;
  onSubmit: (data: Step2Data) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

export function Step2Organization({ initialData, onSubmit, onBack, isSubmitting }: Step2OrganizationProps) {
  const [formData, setFormData] = useState<Step2Data>({
    organization_type: initialData?.organization_type || '',
    organization_name: initialData?.organization_name || '',
    role_title: initialData?.role_title || '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Step2Data, string>>>({});

  const handleChange = (name: keyof Step2Data, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = step2Schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof Step2Data, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof Step2Data;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    await onSubmit(result.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-foreground text-sm font-medium">
          Organization Type *
        </Label>
        <Select
          value={formData.organization_type}
          onValueChange={(value) => handleChange('organization_type', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.organization_type ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {organizationTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.organization_type && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.organization_type}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="organization_name" className="text-foreground text-sm font-medium">
          Organization Name *
        </Label>
        <Input
          id="organization_name"
          value={formData.organization_name}
          onChange={(e) => handleChange('organization_name', e.target.value)}
          placeholder="Department of Health Services"
          className={`mt-1.5 ${errors.organization_name ? 'border-destructive' : ''}`}
        />
        {errors.organization_name && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.organization_name}
          </p>
        )}
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          Your Role / Title *
        </Label>
        <Select
          value={formData.role_title}
          onValueChange={(value) => handleChange('role_title', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.role_title ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {roleOptions.map((role) => (
              <SelectItem key={role} value={role}>{role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.role_title && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.role_title}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-gold hover:bg-gold-light text-dark-bg font-semibold"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
